'use client'

import { useEffect, type RefObject } from 'react'

// Keeps Tab from carrying focus out of an open dialog/drawer/modal into the
// page behind it. Deliberately just the trap — Escape-to-close and initial
// focus differ enough per-component (some already had working versions of
// those) that folding them in here would mean re-deriving each caller's
// existing behavior instead of adding the one piece that was actually
// missing everywhere. Includes `summary` because header.tsx's mobile nav
// (the first place this shape shipped) uses <details><summary> mega-menu
// groups, and a closed <details>'s child links aren't focusable — summary
// is the real first/last tabbable node in that case.
//
// Added 29 Aug 2026: header.tsx got a full hand-rolled version of this; a
// re-audit found four sibling dialogs (help-shell.tsx, help-search.tsx,
// help-ask-ai.tsx, exit-intent-demo.tsx) with role="dialog"/aria-modal but
// no Tab trap, so keyboard users could tab straight through them into
// background content. Factored out here instead of copy-pasting a fifth
// time.
// Each tag clause excludes tabindex="-1" individually — a bare
// `button:not([disabled])` would still match a <button tabIndex={-1}>
// (e.g. a full-bleed backdrop-click target meant to stay out of tab
// order, like exit-intent-demo.tsx's), pulling it into the trap as a
// false first/last element.
const FOCUSABLE_SELECTOR =
  'a[href]:not([tabindex="-1"]), button:not([disabled]):not([tabindex="-1"]), summary:not([tabindex="-1"]), input:not([disabled]):not([tabindex="-1"]), [tabindex]:not([tabindex="-1"])'

export function useFocusTrap(containerRef: RefObject<HTMLElement | null>, active: boolean) {
  useEffect(() => {
    if (!active) return
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Tab') return
      const node = containerRef.current
      if (!node) return
      const focusables = node.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR)
      if (focusables.length === 0) return
      const first = focusables[0]
      const last = focusables[focusables.length - 1]
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault()
        last.focus()
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault()
        first.focus()
      }
    }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [active, containerRef])
}
